import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLightbox } from "@/components/LogoLightbox";

const IHM_LOGO_HD =
  "https://customer-assets.emergentagent.com/job_house-ihm-launch/artifacts/w2dm5l3i_5731B310-CF68-44D4-A984-BA5F338126ED.png";

const linkColumns = [
  {
    title: "Company",
    links: [
      { to: "/about", label: "About" },
      { to: "/vision", label: "Vision" },
      { to: "/directors", label: "Directors" },
    ],
  },
  {
    title: "Ventures",
    links: [
      { to: "/ventures", label: "Skillari" },
      { to: "/ventures", label: "Omnia" },
    ],
  },
  {
    title: "Connect",
    links: [
      { to: "/contact", label: "Contact" },
    ],
  },
];

export default function Footer() {
  const { open } = useLightbox();
  return (
    <footer
      data-testid="site-footer"
      className="bg-white border-t border-[#E5E5EA] mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand block */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="footer-logo"
                onClick={() => open(IHM_LOGO_HD, "House of IHM logo")}
                aria-label="View House of IHM logo fullscreen"
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0E0E10] overflow-hidden cursor-zoom-in transition-transform duration-500 hover:scale-[1.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2997FF] focus-visible:ring-offset-2"
              >
                <img
                  src="/logo.png"
                  alt="House of IHM logo"
                  className="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </button>
              <span className="font-heading font-bold text-[18px] tracking-tight text-[#1D1D1F]">
                House of IHM EdTech
              </span>
            </div>
            <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.22em] text-[#86868B]">
              Where Vision Meets Execution.
            </p>
            <p className="mt-6 text-[15px] leading-relaxed text-[#6E6E73] max-w-md">
              A technology-driven venture building scalable platforms in
              education, intelligence, and human capability.
            </p>
            <a
              href="https://bhmsunrise.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-bhm-tagline"
              className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-[#E5E5EA] bg-[#F5F5F7] px-4 py-2 transition-colors duration-300 hover:bg-white hover:border-[#1D1D1F]"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#2997FF]" />
              <span className="text-[12px] font-mono uppercase tracking-[0.22em] text-[#1D1D1F]">
                Part of BHM Sunrise Pvt. Ltd.
              </span>
              <ArrowUpRight size={12} className="text-[#86868B]" />
            </a>

            <Link
              to="/contact"
              data-testid="footer-cta"
              className="mt-8 inline-flex items-center gap-2 text-[14px] font-medium text-[#1D1D1F] ihm-underline"
            >
              Start a conversation
              <ArrowUpRight size={16} />
            </Link>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {linkColumns.map((col) => (
              <div key={col.title}>
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]">
                  {col.title}
                </p>
                <ul className="mt-5 space-y-3">
                  {col.links.map((l, i) => (
                    <li key={`${col.title}-${i}`}>
                      <Link
                        to={l.to}
                        data-testid={`footer-link-${l.label.toLowerCase()}`}
                        className="text-[14px] text-[#1D1D1F] hover:text-[#2997FF] transition-colors duration-300"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="ihm-divider my-10" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6 gap-1 text-[12px] font-mono uppercase tracking-[0.18em] text-[#86868B]">
            <span data-testid="footer-company-name">
              House of IHM EdTech Private Limited
            </span>
            <span className="hidden sm:block">·</span>
            <span data-testid="footer-incorporated">Incorporated · 20 Jan 2026</span>
            <span className="hidden sm:block">·</span>
            <span data-testid="footer-roc">ROC · Kanpur</span>
          </div>
          <div className="text-[12px] font-mono uppercase tracking-[0.18em] text-[#86868B]">
            <span data-testid="footer-location">Lucknow, Uttar Pradesh, India</span>
          </div>
        </div>

        <div className="mt-6 text-[11px] text-[#86868B]">
          © {new Date().getFullYear()} House of IHM EdTech Private Limited. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
